var m = require('mithril');
var loaders = require('../../layout/components/loaders');
var button = require('../../components/button');
var Model = require('../../libs/Model');
var eventBindings = require('../../../../../models/reports/eventBindings');
var reportEquipmentTable = require('./reportEquipmentTable');
var reportEventPanel = require('./reportEventPanel');
var jsonExportConfig = require('./jsonExportConfig');

var COMPRESSION_OPTIONS = jsonExportConfig.COMPRESSION_OPTIONS;
var normalizeCompression = jsonExportConfig.normalizeCompression;
var normalizeConfig = jsonExportConfig.normalizeConfig;
var buildSavedConfig = jsonExportConfig.buildSavedConfig;

function schemaKey(dataset) {
  return [
    dataset && dataset.className ? dataset.className : 'Equipment',
    Number(dataset && dataset.componentDepth) || 0,
    dataset && dataset.groupKey ? dataset.groupKey : ((dataset && dataset.datasetName) || 'Dataset')
  ].join('::');
}

function buildDatasetGroups(discovery) {
  var groups = new Map();
  (discovery && Array.isArray(discovery.datasets) ? discovery.datasets : []).forEach(function (dataset) {
    if (!dataset) return;
    var key = schemaKey(dataset);
    if (!groups.has(key)) {
      groups.set(key, {
        schemaKey: key,
        className: dataset.className || 'Equipment',
        componentDepth: Number(dataset.componentDepth) || 0,
        groupKey: dataset.groupKey || dataset.datasetName || 'Dataset',
        datasetName: dataset.datasetName || dataset.name || 'Dataset',
        equipmentCount: new Set(),
        columns: new Map()
      });
    }
    var group = groups.get(key);
    if (dataset.equipmentRid) group.equipmentCount.add(dataset.equipmentRid);
    (Array.isArray(dataset.points) ? dataset.points : []).forEach(function (point) {
      var name = String(point && point.name || '').trim();
      if (!name || group.columns.has(name)) return;
      group.columns.set(name, {
        name: name,
        unit: point && point.point ? (point.point.displayUnit || point.point.destUnit || point.point.unit || '') : ''
      });
    });
  });
  return Array.from(groups.values()).map(function (group) {
    group.equipmentCount = group.equipmentCount.size;
    group.columns = Array.from(group.columns.values()).sort(function (a, b) {
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });
    return group;
  }).sort(function (a, b) {
    return (a.className + ' ' + a.datasetName).localeCompare(
      b.className + ' ' + b.datasetName,
      undefined,
      { numeric: true, sensitivity: 'base' }
    );
  });
}

function getEquipmentRids(record) {
  return eventBindings.getReportAttachmentEdges(record).map(function (edge) {
    return edge && edge.in && edge.in['@rid'];
  }).filter(Boolean);
}

function applyConfigToSelection(groups, config) {
  var selection = new Map();
  var configuredByKey = new Map((config.datasets || []).map(function (dataset) {
    return [String(dataset && dataset.schemaKey || ''), dataset];
  }));
  groups.forEach(function (group) {
    var saved = configuredByKey.get(group.schemaKey);
    var columns = config.configured
      ? (saved && Array.isArray(saved.columns) ? saved.columns : [])
      : group.columns.map(function (column) { return column.name; });
    selection.set(group.schemaKey, new Set(columns));
  });
  return selection;
}

function groupSelectionState(state, group) {
  var selected = state.selection.get(group.schemaKey) || new Set();
  if (!selected.size) return 'none';
  return selected.size === group.columns.length ? 'all' : 'some';
}

function toggleGroup(state, group) {
  var current = groupSelectionState(state, group);
  state.selection.set(group.schemaKey, current === 'all'
    ? new Set()
    : new Set(group.columns.map(function (column) { return column.name; })));
  state.dirty = true;
}

function toggleColumn(state, group, columnName) {
  var selected = new Set(state.selection.get(group.schemaKey) || []);
  if (selected.has(columnName)) selected.delete(columnName);
  else selected.add(columnName);
  state.selection.set(group.schemaKey, selected);
  state.dirty = true;
}

function selectionIcon(selectionState) {
  if (selectionState === 'all') return 'ion-android-checkbox blue';
  if (selectionState === 'some') return 'ion-android-checkbox-outline blue';
  return 'ion-android-checkbox-outline-blank moon-gray';
}

function loadFields(vnode) {
  var state = vnode.state;
  var record = vnode.attrs.record();
  var equipmentRids = getEquipmentRids(record);
  state.equipmentSignature = equipmentRids.slice().sort().join(',');
  state.loading = true;
  state.error = '';
  if (!equipmentRids.length) {
    state.groups = [];
    state.selection = new Map();
    state.loading = false;
    state.dirty = false;
    return Promise.resolve();
  }
  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/entities/field-discovery',
    method: 'GET',
    background: true,
    params: {
      customEquipment: equipmentRids.join(','),
      summaryOnly: 0,
      componentDepthLimit: 0
    }
  }).then(function (discovery) {
    state.groups = buildDatasetGroups(discovery);
    var config = normalizeConfig(record && record.exportConfig);
    state.selection = applyConfigToSelection(state.groups, config);
    state.compression = config.compression;
    state.dirty = false;
  }).catch(function (error) {
    state.groups = [];
    state.selection = new Map();
    state.error = error && (error.error || error.message) ? (error.error || error.message) : 'Could not load export fields.';
  }).finally(function () {
    state.loading = false;
    m.redraw();
  });
}

function saveFields(vnode) {
  var state = vnode.state;
  var recordStream = vnode.attrs.record;
  var record = recordStream();
  var config = buildSavedConfig(state.groups, state.selection, state.compression);
  var previousConfig = record.exportConfig;
  record.exportConfig = config;
  recordStream(record);
  state.saving = true;
  state.error = '';
  return loaders.requestWithLoader({
    loader: 'submit',
    url: Model.urlForRecord(record),
    method: 'PUT',
    body: { exportConfig: config }
  }).then(function (updatedRecord) {
    if (updatedRecord) recordStream(updatedRecord);
    state.dirty = false;
  }).catch(function (error) {
    record.exportConfig = previousConfig;
    recordStream(record);
    state.error = error && (error.error || error.message) ? (error.error || error.message) : 'Could not save export fields.';
    return Promise.reject(error);
  }).finally(function () {
    state.saving = false;
    m.redraw();
  });
}

var component = {
  oninit: function (vnode) {
    vnode.state.groups = [];
    vnode.state.selection = new Map();
    vnode.state.loading = false;
    vnode.state.saving = false;
    vnode.state.dirty = false;
    vnode.state.error = '';
    vnode.state.search = '';
    vnode.state.compression = normalizeConfig(vnode.attrs.record() && vnode.attrs.record().exportConfig).compression;
    vnode.state.equipmentSignature = '';
    loadFields(vnode);
  },
  onbeforeupdate: function (vnode) {
    var signature = getEquipmentRids(vnode.attrs.record()).slice().sort().join(',');
    if (signature !== vnode.state.equipmentSignature && !vnode.state.loading) {
      loadFields(vnode);
    }
    return true;
  },
  view: function (vnode) {
    var state = vnode.state;
    var record = vnode.attrs.record();
    var search = String(state.search || '').trim().toLowerCase();
    var visibleGroups = state.groups.filter(function (group) {
      if (!search) return true;
      if ((group.className + ' ' + group.datasetName).toLowerCase().indexOf(search) >= 0) return true;
      return group.columns.some(function (column) { return column.name.toLowerCase().indexOf(search) >= 0; });
    });
    var selectedCount = Array.from(state.selection.values()).reduce(function (count, selected) {
      return count + selected.size;
    }, 0);

    return m('div', [
      m(reportEventPanel, {record: record, showLocation: false}),
      m('section.mh3.mb4', [
        m('h2.f1.fw3.mt0.mb3', 'Output'),
        m('label.db.f4.mb2', {for: 'json-export-compression'}, 'Compression'),
        m('select#json-export-compression.input-reset.ba.b--black-20.pa2.w-100.w5-l', {
          value: state.compression,
          disabled: state.saving || state.loading,
          onchange: function (event) {
            state.compression = normalizeCompression(event.target.value);
            state.dirty = true;
            return saveFields(vnode);
          }
        }, COMPRESSION_OPTIONS.map(function (option) {
          return m('option', {value: option.value}, option.label);
        })),
        m('div.f6.moon-gray.mt2', state.compression === 'automatic'
          ? 'Gzip JSON files larger than 5 MiB.'
          : (state.compression === 'gzip'
            ? 'Always create a .json.gz file.'
            : 'Always create an uncompressed .json file.')),
        m('div.f6.moon-gray.mt1', state.saving ? 'Saving compression setting...' : 'Compression changes save automatically.')
      ]),
      m(reportEquipmentTable, {
        record: vnode.attrs.record,
        showEventPanel: false,
        showAuxiliaryPoints: false
      }),
      m('section.mh3.mb4', [
        m('div.flex.items-end.justify-between.flex-wrap.mb3', [
          m('div.mr3', [
            m('h2.f1.fw3.mv0', 'Fields to export'),
            m('div.f5.moon-gray.mt1', selectedCount + ' selected column' + (selectedCount === 1 ? '' : 's'))
          ]),
          m(button, {
            label: state.saving ? 'Saving...' : 'Save Selection',
            class: 'btn-good green dib',
            disabled: state.saving || !state.dirty,
            func: function () { return saveFields(vnode); }
          })
        ]),
        getEquipmentRids(record).length ? m('input.input-reset.ba.b--black-20.pa2.mb3.w-100', {
          type: 'search',
          placeholder: 'Filter datasets or columns',
          value: state.search,
          oninput: function (event) { state.search = event.target.value; }
        }) : null,
        state.error ? m('div.red.mb3', state.error) : null,
        state.loading ? m('div.moon-gray.pa3', 'Loading datasets and columns...') : null,
        !state.loading && !getEquipmentRids(record).length
          ? m('div.pa3.ba.b--mid-gray.br2.moon-gray', 'Add equipment to choose datasets and columns.')
          : null,
        !state.loading && getEquipmentRids(record).length && !visibleGroups.length
          ? m('div.pa3.ba.b--mid-gray.br2.moon-gray', search ? 'No matching datasets or columns.' : 'No datasets are available for the included equipment.')
          : null,
        visibleGroups.map(function (group) {
          var groupState = groupSelectionState(state, group);
          var selected = state.selection.get(group.schemaKey) || new Set();
          return m('div.ba.b--mid-gray.br2.mb3.overflow-hidden', {key: group.schemaKey}, [
            m('button.input-reset.bn.bg-near-black.white.w-100.tl.pa3.pointer', {
              type: 'button',
              onclick: function () { toggleGroup(state, group); }
            }, [
              m('div.flex.items-center.justify-between', [
                m('div', [
                  m('div.f4', group.datasetName),
                  m('div.f6.moon-gray.mt1', group.className + ' · ' + group.equipmentCount + ' equipment item' + (group.equipmentCount === 1 ? '' : 's'))
                ]),
                m('i.f3', {class: selectionIcon(groupState)})
              ])
            ]),
            m('div', group.columns.filter(function (column) {
              return !search || (group.className + ' ' + group.datasetName + ' ' + column.name).toLowerCase().indexOf(search) >= 0;
            }).map(function (column) {
              var isSelected = selected.has(column.name);
              return m('button.input-reset.bn.bg-transparent.white.w-100.tl.ph3.pv2.pointer.bb.b--near-black', {
                type: 'button',
                key: column.name,
                onclick: function () { toggleColumn(state, group, column.name); }
              }, [
                m('div.flex.items-center.justify-between', [
                  m('span', [column.name, column.unit ? m('span.moon-gray.ml2', column.unit) : null]),
                  m('i.f4', {class: isSelected ? 'ion-android-checkbox blue' : 'ion-android-checkbox-outline-blank moon-gray'})
                ])
              ]);
            }))
          ]);
        }),
        m('div.flex.justify-end.mt3', [
          m(button, {
            label: state.saving ? 'Saving...' : 'Save Selection',
            class: 'btn-good green dib',
            disabled: state.saving || !state.dirty,
            func: function () { return saveFields(vnode); }
          })
        ])
      ])
    ]);
  }
};

component.__testables = {
  applyConfigToSelection: applyConfigToSelection,
  buildDatasetGroups: buildDatasetGroups,
  buildSavedConfig: buildSavedConfig,
  normalizeCompression: normalizeCompression,
  normalizeConfig: normalizeConfig,
  schemaKey: schemaKey
};

module.exports = component;
